/**
 * UK Coverage Map Component
 * Displays a map of UK counties with active service areas highlighted
 */

import { useEffect, useRef } from 'react'
import ukMapSvg from '../assets/images/united_kingdom.svg'

// Active region IDs from the SVG
// Greater London boroughs
const LONDON_BOROUGH_IDS = [
  'GBBDG', // Barking and Dagenham
  'GBBEN', // Brent
  'GBBEX', // Bexley
  'GBBNE', // Barnet
  'GBBRY', // Bromley
  'GBCMD', // Camden
  'GBCRY', // Croydon
  'GBEAL', // Ealing
  'GBENF', // Enfield
  'GBGRE', // Greenwich
  'GBHAV', // Havering
  'GBHCK', // Hackney
  'GBHIL', // Hillingdon
  'GBHMF', // Hammersmith and Fulham
  'GBHNS', // Hounslow
  'GBHRW', // Harrow
  'GBHRY', // Haringey
  'GBISL', // Islington
  'GBKEC', // Kensington and Chelsea
  'GBKTT', // Kingston upon Thames
  'GBLBH', // Lambeth
  'GBLEW', // Lewisham
  'GBLND', // City of London
  'GBMRT', // Merton
  'GBNWM', // Newham
  'GBRDB', // Redbridge
  'GBRIC', // Richmond upon Thames
  'GBSTN', // Sutton
  'GBSWK', // Southwark
  'GBTWH', // Tower Hamlets
  'GBWFT', // Waltham Forest
  'GBWND', // Wandsworth
  'GBWSM', // Westminster
]

// Other active counties
const OTHER_ACTIVE_IDS = [
  'GBKEN', // Kent
  'GBESS', // Essex
  'GBWSX', // W Sussex
  'GBESX', // E Sussex  
  'GBHRT', // Hertfordshire
  'GBSRY' // Surrey
]

const ALL_ACTIVE_IDS = [...LONDON_BOROUGH_IDS, ...OTHER_ACTIVE_IDS]

// Display names for legend
const SERVICE_AREAS = [
  'Greater London',
  'Essex',
  'Hertfordshire',
  'Kent',
  'Surrey',
  'Sussex'
]

interface UKCoverageMapProps {
  className?: string
  showLegend?: boolean
  title?: string
  subtitle?: string
}

const UKCoverageMap = ({
  className = '',
  showLegend = true,
  title = 'Our Service Areas',
  subtitle = 'During our UK rollout, we\'re prioritising key UK regions to ensure our high quality service. If your location isn\'t shown, we may still be able to serve you.',
}: UKCoverageMapProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const loadAndStyleMap = async () => {
      if (!mapContainerRef.current) return

      try {
        // Fetch the SVG content
        const response = await fetch(ukMapSvg)
        const svgText = await response.text()

        // Insert the SVG into the container
        mapContainerRef.current.innerHTML = svgText

        // Get the SVG element
        const svgElement = mapContainerRef.current.querySelector('svg')
        if (!svgElement) return

        // Style the SVG
        svgElement.style.width = '100%'
        svgElement.style.height = 'auto'
        svgElement.style.maxHeight = '500px'

        // Style all paths (default inactive state)
        const allPaths = svgElement.querySelectorAll('path')
        allPaths.forEach((path) => {
          path.style.fill = '#e5e7eb'
          path.style.stroke = '#d1d5db'
          path.style.strokeWidth = '0.5'
          path.style.transition = 'fill 0.3s ease'
        })

        // Style all circles (for small regions)
        const allCircles = svgElement.querySelectorAll('circle')
        allCircles.forEach((circle) => {
          circle.style.fill = '#e5e7eb'
          circle.style.stroke = '#d1d5db'
          circle.style.strokeWidth = '0.5'
          circle.style.transition = 'fill 0.3s ease'
        })

        // Highlight active regions
        ALL_ACTIVE_IDS.forEach((id) => {
          const element = svgElement.querySelector(`#${id}`)
          if (element) {
            ;(element as SVGElement).style.fill = '#10b981'
            ;(element as SVGElement).style.stroke = '#059669'
          }
        })
      } catch (error) {
        console.error('Error loading UK map:', error)
      }
    }

    loadAndStyleMap()
  }, [])

  return (
    <div className={`${className}`}>
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold text-dark-plum mb-4">
          {title}
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          {subtitle}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
        {/* Map Container */}
        <div
          ref={mapContainerRef}
          className="w-full max-w-md lg:max-w-lg"
          aria-label="Map of UK showing service coverage areas"
        />

        {/* Legend */}
        {showLegend && (
          <div className="w-full max-w-sm">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-dark-plum mb-4">
                Currently Serving
              </h3>
              <ul className="space-y-2">
                {SERVICE_AREAS.map((area) => (
                  <li key={area} className="flex items-center gap-3">
                    <span className="w-3 h-3 bg-primary-green rounded-full flex-shrink-0"></span>
                    <span className="text-gray-700">{area}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="w-3 h-3 bg-gray-200 rounded-full flex-shrink-0"></span>
                  <span>Coming soon to more areas</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default UKCoverageMap
